import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { IconType } from "react-icons";
import {
  FiActivity, FiAlertTriangle, FiCheckCircle, FiChevronDown, FiChevronUp, FiClock, FiCopy, FiDownload, FiExternalLink,
  FiFilter, FiHelpCircle, FiLink, FiMinus, FiMoreHorizontal, FiPower, FiRadio, FiSearch, FiServer, FiWifiOff, FiX, FiXCircle,
} from "react-icons/fi";

import {
  getOverview, getSubscriber, linkSubscriberONT, listSubscribers, OVERVIEW_REFRESH_MS, subscriberCandidates, unlinkSubscriberONT,
  type ONTCandidate,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSignalBands } from "@/lib/bands";
import { integrationHealth } from "@/lib/integrations";
import { fmtDbm } from "@/lib/signal";
import {
  baseFilter, odpIssues, odpsOf, ontNet, quickTest, rowOf, sortRows, viewFromParams, viewToParams,
  type Attention, type Ctx, type Net, type OntNet, type Quick, type Row, type SortKey, type Svc, type View,
} from "@/lib/subscribers";
import type { Subscriber, SubscriberONT } from "@/lib/types";
import { useIntegrations, useNow } from "@/components/MonitoringBanner";
import { distText, durText } from "@/components/OntStatus";
import { eventTitle } from "@/routes/ONTDetailPage";
import CustomerPingDialog from "@/components/CustomerPing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDateTime, fmtNum, useI18n, type Key } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type T = (k: Key, p?: Record<string, string | number>) => string;

// ---------- visual vocabulary ----------

type Tone = "good" | "warn" | "crit" | "muted" | "info";
const TONE: Record<Tone, { text: string; bg: string; dot: string; border: string }> = {
  good: { text: "text-signal-good", bg: "bg-signal-good/10", dot: "bg-signal-good", border: "border-l-signal-good" },
  warn: { text: "text-signal-warn", bg: "bg-amber-400/10", dot: "bg-signal-warn", border: "border-l-signal-warn" },
  crit: { text: "text-signal-crit", bg: "bg-signal-crit/10", dot: "bg-signal-crit", border: "border-l-signal-crit" },
  muted: { text: "text-slate-400", bg: "bg-slate-700/40", dot: "bg-slate-500", border: "border-l-transparent" },
  info: { text: "text-signal", bg: "bg-signal/10", dot: "bg-signal", border: "border-l-signal" },
};

const NET_UI: Record<Net, { tone: Tone; icon: IconType }> = {
  online: { tone: "good", icon: FiCheckCircle },
  weak: { tone: "warn", icon: FiAlertTriangle },
  critical: { tone: "crit", icon: FiAlertTriangle },
  los: { tone: "crit", icon: FiWifiOff },
  power: { tone: "warn", icon: FiPower },
  offline: { tone: "crit", icon: FiXCircle },
  stale: { tone: "muted", icon: FiClock },
  cpe_online: { tone: "info", icon: FiRadio },
  cpe_silent: { tone: "warn", icon: FiRadio },
  unknown: { tone: "muted", icon: FiHelpCircle },
  unlinked: { tone: "muted", icon: FiMinus },
};

const SVC_TONE: Record<Svc, Tone> = { active: "good", suspended: "warn", terminated: "muted", cancelled: "muted", other: "muted" };
const ATT_TONE: Record<Attention, Tone> = { activeDown: "crit", activeSignal: "warn", activeSilent: "warn", suspendedOnline: "warn", endedLinked: "muted" };

function Badge({ tone, icon: I, children, quiet }: { tone: Tone; icon?: IconType; children: ReactNode; quiet?: boolean }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold tracking-[0.12em] uppercase",
      TONE[tone].text, quiet ? "bg-transparent px-0" : TONE[tone].bg)}>
      {I && <I className="size-3" />} {children}
    </span>
  );
}

const Dot = ({ tone }: { tone: Tone }) => <span className={cn("inline-block size-1.5 shrink-0 rounded-full", TONE[tone].dot)} />;

const svcLabel = (s: Subscriber, svc: Svc, t: T) => (svc === "other" ? s.status.toUpperCase() : t(`sub.svc.${svc}` as Key));
const dur = (ms: number | null | undefined) => (ms === null || ms === undefined ? "" : durText(ms));
const path = (o: Pick<SubscriberONT, "olt_name" | "pon_path" | "ont_id">) => `${shortOlt(o.olt_name)} · ${o.pon_path} · #${o.ont_id}`;
/** "C-Data FD1602S-B1" -> "C-Data": the vendor is what tells two OLTs apart at a glance. */
const shortOlt = (name: string | null | undefined) => (name ?? "?").split(/\s+/)[0];

function attText(a: Attention, r: Row, t: T): string {
  const d = dur(r.n.forMs);
  switch (a) {
    case "activeDown": return d ? t("sub.att.activeDownFor", { d }) : t("sub.att.activeDown");
    case "activeSignal": return t("sub.att.activeSignal");
    case "activeSilent": return d ? t("sub.att.activeSilentFor", { d }) : t("sub.att.activeSilent");
    case "suspendedOnline": return t("sub.att.suspendedOnline");
    case "endedLinked": return t("sub.att.endedLinked");
  }
}

// ---------- page ----------

export default function SubscribersPage() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const canLink = user?.role === "admin" || user?.role === "noc";
  const [sp, setSp] = useSearchParams();
  const v = viewFromParams(sp);
  const openId = Number(sp.get("open")) || null;
  const setView = (patch: Partial<View>, keepPage = false) => {
    const next = viewToParams({ ...v, ...patch, page: keepPage ? (patch.page ?? v.page) : 0 });
    if (openId) next.set("open", String(openId));
    setSp(next, { replace: true });
  };
  const setOpen = (id: number | null) => {
    const p = new URLSearchParams(sp);
    if (id) p.set("open", String(id)); else p.delete("open");
    setSp(p, { replace: true });
  };

  const now = useNow(30_000);
  const bands = useSignalBands();
  const subs = useQuery({ queryKey: ["subscribers"], queryFn: listSubscribers, refetchInterval: 60_000 });
  const ov = useQuery({ queryKey: ["overview"], queryFn: getOverview, refetchInterval: OVERVIEW_REFRESH_MS });
  const ints = useIntegrations();
  const ctx: Ctx = { pollS: ov.data?.olt_poll_interval_s ?? 120, now, bands };
  const all = useMemo(() => (subs.data?.subscribers ?? []).map((s) => rowOf(s, ctx)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subs.data, ctx.pollS, now, bands.warn, bands.crit]);

  const [q, setQ] = useState(v.q);
  useEffect(() => setQ(v.q), [v.q]);
  useEffect(() => {
    if (q === v.q) return;
    const id = setTimeout(() => setView({ q }), 200);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const [filtersOpen, setFiltersOpen] = useState(v.svc.length + v.net.length > 0 || !!(v.olt || v.pon || v.plan || v.odp || v.link));
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [linkFor, setLinkFor] = useState<Subscriber | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === "/" && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);

  const base = baseFilter(all, v);
  const issues = odpIssues(all);
  const rows = sortRows(base.filter(quickTest[v.quick]), v.sort, v.dir);
  const pages = Math.max(1, Math.ceil(rows.length / v.size));
  const page = Math.min(v.page, pages - 1);
  const shown = rows.slice(page * v.size, (page + 1) * v.size);
  const open = all.find((r) => r.s.id === openId) ?? null;

  // Header counts are over everything, independent of the view.
  const n = (f: (r: Row) => boolean) => all.filter(f).length;
  const svcCounts = (["active", "suspended", "terminated", "cancelled", "other"] as Svc[]).map((s) => [s, n((r) => r.svc === s)] as const).filter(([, c]) => c);
  const onOnt = n((r) => r.s.onts.length > 0), cpeOnly = n((r) => r.n.source === "cpe"), none = n((r) => r.n.source === "none");

  const whmcs = ints.data?.integrations.find((i) => i.name === "whmcs");
  const wh = whmcs ? integrationHealth(whmcs, now) : null;
  const whTone: Tone = !wh ? "muted" : wh.state === "healthy" || wh.state === "running" ? "good" : wh.state === "failed" ? "crit" : "warn";

  const exportCsv = () => {
    const pick = sel.size ? rows.filter((r) => sel.has(r.s.id)) : rows;
    const esc = (x: unknown) => `"${String(x ?? "").replace(/"/g, '""')}"`;
    const head = ["customer_code", "name", "service", "speed", "service_status", "network", "rx_dbm", "olt", "pon", "ont", "ont_serial", "cpe_serial", "attention"];
    const lines = pick.map((r) => {
      const o = r.n.primary?.ont;
      return [r.s.customer_code, r.s.full_name, r.plan.name, r.plan.speed, r.svc, r.n.net, r.n.rx, o?.olt_name, o?.pon_path, o?.ont_id, o?.serial, r.s.cpe?.serial, r.att]
        .map(esc).join(",");
    });
    const url = URL.createObjectURL(new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `subscribers-${new Date().toISOString().slice(0, 10)}.csv` });
    a.click();
    URL.revokeObjectURL(url);
  };

  const quicks: { k: Quick; tone?: Tone }[] = [
    { k: "attention", tone: "warn" }, { k: "active" }, { k: "suspended" }, { k: "unlinked" }, { k: "offline", tone: "crit" }, { k: "weak", tone: "warn" }, { k: "all" },
  ];
  const activeFilters = v.svc.length + v.net.length + (v.olt ? 1 : 0) + (v.pon ? 1 : 0) + (v.plan ? 1 : 0) + (v.odp ? 1 : 0) + (v.link ? 1 : 0);
  const sortBy = (k: SortKey, first: "asc" | "desc" = "asc") => setView({ sort: k, dir: v.sort === k ? (v.dir === "asc" ? "desc" : "asc") : first }, true);
  const allOnPage = shown.length > 0 && shown.every((r) => sel.has(r.s.id));

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-1">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">{t("sub.title")}</h1>
          <p className="data mt-1 flex flex-wrap items-center gap-x-2 text-[12.5px] text-slate-400">
            <span className="text-slate-200">{t("sub.count", { n: fmtNum(locale, all.length) })}</span>
            {svcCounts.map(([s, c]) => <span key={s}>· {fmtNum(locale, c)} {t(`sub.svcLower.${s}` as Key)}</span>)}
            <span className="text-slate-600">|</span>
            <span>{t("sub.onOnt", { n: fmtNum(locale, onOnt) })}</span>
            <span>· {t("sub.cpeOnly", { n: fmtNum(locale, cpeOnly) })}</span>
            <span>· {t("sub.unlinkedN", { n: fmtNum(locale, none) })}</span>
          </p>
        </div>
        <p className="flex items-center gap-2 text-[12px] text-slate-400" title={whmcs?.last_run.detail ?? undefined}>
          <span className="eyebrow">{t("sub.source")}</span>
          <span className="text-slate-200">WHMCS</span>
          <Dot tone={whTone} />
          {whmcs?.last_ok_at ? t("sub.syncedAgo", { age: durText(now - Date.parse(whmcs.last_ok_at)) }) : t("sub.neverSynced")}
          {wh && wh.state !== "healthy" && wh.state !== "running" && <span className={TONE[whTone].text}>· {t(`sub.sync.${wh.state}` as Key)}</span>}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div role="tablist" className="flex rounded-sm border border-rule p-0.5">
          {(["operational", "all"] as const).map((s) => (
            <button key={s} role="tab" type="button" aria-selected={v.scope === s} onClick={() => setView({ scope: s })}
              className={cn("rounded-sm px-3 py-1 text-[12.5px]", v.scope === s ? "bg-signal/10 text-slate-100" : "text-slate-400 hover:text-slate-100")}>
              {t(`sub.scope.${s}` as Key)}
            </button>
          ))}
        </div>
        <div className="relative w-full max-w-[440px] flex-1">
          <FiSearch className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-slate-500" />
          <Input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("sub.search")} aria-label={t("sub.search")}
            className="h-8 border-rule bg-panel pr-8 pl-8 text-[12.5px]" />
          {q ? (
            <button type="button" aria-label={t("sub.clearSearch")} onClick={() => setQ("")} className="absolute top-1/2 right-2 -translate-y-1/2 text-slate-500 hover:text-slate-200"><FiX className="size-3.5" /></button>
          ) : <kbd className="absolute top-1/2 right-2 -translate-y-1/2 rounded border border-rule px-1 text-[10px] text-slate-500">/</kbd>}
        </div>
        <button type="button" aria-expanded={filtersOpen} onClick={() => setFiltersOpen(!filtersOpen)}
          className={cn("inline-flex h-8 items-center gap-1.5 rounded-sm border px-2.5 text-[12.5px]", filtersOpen || activeFilters ? "border-signal/50 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100")}>
          <FiFilter className="size-3.5" /> {t("sub.filters")}{activeFilters > 0 && <span className="data text-signal">{activeFilters}</span>}
        </button>
        <span className="flex-1" />
        <Pager v={v} rows={rows.length} page={page} pages={pages} setView={setView} compact />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {quicks.map(({ k, tone }) => {
          const c = base.filter(quickTest[k]).length;
          return (
            <button key={k} type="button" aria-pressed={v.quick === k} onClick={() => setView({ quick: k })}
              className={cn("rounded-sm border px-2 py-1 text-[12px]", v.quick === k ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100")}>
              {k === "attention" && <FiAlertTriangle className="mr-1 inline size-3 -translate-y-px text-signal-warn" />}
              {t(`sub.quick.${k}` as Key)} <span className={cn("data", tone && c ? TONE[tone].text : "text-slate-500")}>{fmtNum(locale, c)}</span>
            </button>
          );
        })}
      </div>

      {issues.map((x) => (
        <div key={x.odp} className="flex items-center gap-3 rounded-sm border border-amber-400/30 bg-amber-400/[0.05] px-3 py-1.5 text-[12.5px]">
          <FiAlertTriangle className="size-3.5 shrink-0 text-signal-warn" />
          <span className="text-amber-100"><b className="font-semibold">{t("sub.odp.issue")}</b> · <span className="data">{x.odp}</span> · {t("sub.odp.down", { n: x.down, total: x.total })}</span>
          <span className="hidden min-w-0 truncate text-slate-400 2xl:inline" title={t("sub.odp.hint")}>{t("sub.odp.hint")}</span>
          <button type="button" onClick={() => setView({ odp: x.odp, quick: "all" })} className="ml-auto shrink-0 text-signal hover:underline">{t("sub.odp.show")}</button>
        </div>
      ))}

      {filtersOpen && <Filters v={v} all={all} setView={setView} />}

      {sel.size > 0 && (
        <div className="flex items-center gap-3 rounded-sm border border-signal/30 bg-signal/[0.05] px-3 py-1.5 text-[12.5px]">
          <span className="text-slate-100">{t("sub.selected", { n: sel.size })}</span>
          <Button size="xs" variant="outline" onClick={exportCsv}><FiDownload /> {t("sub.export")}</Button>
          <Button size="xs" variant="outline" onClick={() => navigator.clipboard?.writeText(rows.filter((r) => sel.has(r.s.id)).map((r) => r.s.customer_code ?? r.s.id).join("\n"))}><FiCopy /> {t("sub.copyIds")}</Button>
          <button type="button" onClick={() => setSel(new Set())} className="ml-auto text-slate-400 hover:text-slate-100">{t("sub.clearSel")}</button>
        </div>
      )}

      {subs.error && <p className="text-signal-crit">{(subs.error as Error).message}</p>}
      {subs.isLoading && <p className="text-slate-400">{t("common.loading")}</p>}

      {subs.data && (
        <div className="overflow-x-auto rounded-lg border border-rule bg-panel">
          <table className="w-full min-w-[1180px] table-fixed text-[13px]">
            <thead>
              <tr className="border-b border-rule text-left">
                <th className="w-9 pl-3">
                  <input type="checkbox" className="accent-signal [color-scheme:dark]" aria-label={t("sub.selectPage")} checked={allOnPage}
                    onChange={() => setSel((s0) => { const s = new Set(s0); shown.forEach((r) => (allOnPage ? s.delete(r.s.id) : s.add(r.s.id))); return s; })} />
                </th>
                <Th w="w-[21%]" k="name" v={v} sortBy={sortBy} label={t("sub.col.subscriber")} />
                <Th w="w-[16%]" k="service" v={v} sortBy={sortBy} label={t("sub.col.service")} />
                <Th w="w-[15%]" k="status" v={v} sortBy={sortBy} label={t("sub.col.svcStatus")} />
                <Th w="w-[21%]" k="network" v={v} sortBy={sortBy} label={t("sub.col.network")} />
                <Th w="w-[12%]" k="signal" v={v} sortBy={sortBy} label={t("sub.col.signal")} />
                <th className="eyebrow w-[12%] px-3 py-2.5 font-medium">{t("sub.col.access")}</th>
                <th className="w-11" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <SubRow key={r.s.id} r={r} now={now} selected={sel.has(r.s.id)} canLink={canLink}
                  onSelect={() => setSel((s0) => { const s = new Set(s0); if (s.has(r.s.id)) s.delete(r.s.id); else s.add(r.s.id); return s; })}
                  onOpen={() => setOpen(r.s.id)} onLink={() => setLinkFor(r.s)} />
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                  {t("sub.noMatch")}{v.scope === "operational" && <> · <button type="button" className="text-signal hover:underline" onClick={() => setView({ scope: "all" })}>{t("sub.searchAll")}</button></>}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {subs.data && rows.length > 0 && (
        <div className="flex items-center justify-between gap-3 text-[12px] text-slate-400">
          <button type="button" onClick={exportCsv} className="inline-flex items-center gap-1.5 hover:text-slate-100"><FiDownload className="size-3.5" /> {t("sub.exportView", { n: rows.length })}</button>
          <Pager v={v} rows={rows.length} page={page} pages={pages} setView={setView} />
        </div>
      )}

      {open && <Drawer r={open} ctx={ctx} canLink={canLink} onClose={() => setOpen(null)} onLink={() => setLinkFor(open.s)} />}
      {linkFor && <LinkDialog s={linkFor} ctx={ctx} onClose={() => setLinkFor(null)} />}
    </div>
  );
}

function Th({ w, k, v, sortBy, label }: { w: string; k: SortKey; v: View; sortBy: (k: SortKey, first?: "asc" | "desc") => void; label: string }) {
  const on = v.sort === k;
  const I = on && v.dir === "desc" ? FiChevronDown : FiChevronUp;
  return (
    <th className={cn("px-3 py-2.5 font-medium", w)} aria-sort={on ? (v.dir === "asc" ? "ascending" : "descending") : undefined}>
      <button type="button" onClick={() => sortBy(k)} className={cn("eyebrow inline-flex items-center gap-1 hover:text-slate-200", on && "text-slate-200")}>
        {label} <I className={cn("size-3", on ? "opacity-100" : "opacity-0")} />
      </button>
    </th>
  );
}

function Pager({ v, rows, page, pages, setView, compact }: { v: View; rows: number; page: number; pages: number; setView: (p: Partial<View>, keep?: boolean) => void; compact?: boolean }) {
  const { t, locale } = useI18n();
  if (!rows) return null;
  return (
    <div className="flex items-center gap-2 text-[12px] text-slate-400">
      <span className="data">{t("sub.showing", { from: fmtNum(locale, page * v.size + 1), to: fmtNum(locale, Math.min(rows, (page + 1) * v.size)), n: fmtNum(locale, rows) })}</span>
      {!compact && (
        <select aria-label={t("sub.perPage")} value={v.size} onChange={(e) => setView({ size: Number(e.target.value) })}
          className="h-7 rounded-sm border border-rule bg-panel px-1.5 text-[12px] text-slate-300">
          {[25, 50, 100].map((n) => <option key={n} value={n}>{t("sub.perPageN", { n })}</option>)}
        </select>
      )}
      <Button size="xs" variant="outline" aria-label={t("sub.prev")} disabled={page === 0} onClick={() => setView({ page: page - 1 }, true)}>‹</Button>
      <Button size="xs" variant="outline" aria-label={t("sub.next")} disabled={page >= pages - 1} onClick={() => setView({ page: page + 1 }, true)}>›</Button>
    </div>
  );
}

// ---------- filters ----------

function Filters({ v, all, setView }: { v: View; all: Row[]; setView: (p: Partial<View>) => void }) {
  const { t } = useI18n();
  const olts = [...new Map(all.flatMap((r) => r.s.onts.map((o) => [String(o.olt_id), o.olt_name] as const))).entries()];
  const pons = [...new Set(all.flatMap((r) => r.s.onts.filter((o) => !v.olt || String(o.olt_id) === v.olt).map((o) => `${o.olt_id}:${o.pon_path}`)))].sort();
  const plans = [...new Set(all.map((r) => r.plan.name).filter(Boolean))].sort();
  const odps = [...new Set(all.flatMap((r) => odpsOf(r.s)))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const toggle = <T,>(xs: T[], x: T) => (xs.includes(x) ? xs.filter((y) => y !== x) : [...xs, x]);
  const chip = (on: boolean, label: string, onClick: () => void) => (
    <button key={label} type="button" aria-pressed={on} onClick={onClick}
      className={cn("rounded-sm border px-2 py-0.5 text-[12px]", on ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100")}>{label}</button>
  );
  const sel = (label: string, value: string, onChange: (x: string) => void, opts: [string, string][], allLabel: string) => (
    <label className="flex items-center gap-2 text-[12px] text-slate-500">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} className="h-7 max-w-52 rounded-sm border border-rule bg-panel px-1.5 text-[12px] text-slate-200">
        <option value="">{allLabel}</option>
        {opts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </label>
  );
  const nets: Net[] = ["online", "weak", "critical", "los", "power", "offline", "stale", "cpe_online", "cpe_silent", "unknown", "unlinked"];
  return (
    <div className="grid gap-2 rounded-sm border border-rule bg-panel/60 px-3 py-2.5 text-[12px]">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="eyebrow w-32">{t("sub.col.svcStatus")}</span>
        {(["active", "suspended", "terminated", "cancelled"] as Svc[]).map((s) => chip(v.svc.includes(s), t(`sub.svc.${s}` as Key), () => setView({ svc: toggle(v.svc, s), scope: s === "terminated" || s === "cancelled" ? "all" : v.scope })))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="eyebrow w-32">{t("sub.col.network")}</span>
        {nets.map((x) => chip(v.net.includes(x), t(`sub.net.${x}` as Key), () => setView({ net: toggle(v.net, x) })))}
      </div>
      <div className="flex flex-wrap items-center gap-4">
        {sel(t("sub.f.olt"), v.olt, (x) => setView({ olt: x, pon: "" }), olts.map(([id, n]) => [id, n]), t("sub.f.allOlts"))}
        {sel(t("sub.f.pon"), v.pon, (x) => setView({ pon: x }), pons.map((p) => [p, p.split(":")[1]]), t("sub.f.allPons"))}
        {sel(t("sub.f.plan"), v.plan, (x) => setView({ plan: x }), plans.map((p) => [p, p]), t("sub.f.allPlans"))}
        {odps.length > 0 && sel("ODP", v.odp, (x) => setView({ odp: x }), odps.map((p) => [p, p]), t("sub.f.allOdps"))}
        {sel(t("sub.f.link"), v.link, (x) => setView({ link: x as View["link"] }), [["linked", t("sub.f.linked")], ["unlinked", t("sub.f.noOnt")]], t("common.all"))}
        <button type="button" onClick={() => setView({ svc: [], net: [], olt: "", pon: "", plan: "", odp: "", link: "" })} className="ml-auto text-slate-400 hover:text-slate-100">{t("sub.f.clear")}</button>
      </div>
    </div>
  );
}

// ---------- row ----------

function SubRow({ r, now, selected, canLink, onSelect, onOpen, onLink }: {
  r: Row; now: number; selected: boolean; canLink: boolean; onSelect: () => void; onOpen: () => void; onLink: () => void;
}) {
  const { t } = useI18n();
  const { s, n, att, plan, svc } = r;
  const ui = NET_UI[n.net];
  const p = n.primary;
  const ended = svc === "terminated" || svc === "cancelled";
  return (
    <tr onClick={onOpen} className={cn("group cursor-pointer border-b border-l-2 border-rule/60 last:border-b-0 hover:bg-white/[0.02]",
      att ? TONE[ATT_TONE[att]].border : "border-l-transparent", selected && "bg-signal/[0.04]", ended && !att && "opacity-60")}>
      <td className="pl-3" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" className="accent-signal [color-scheme:dark]" aria-label={t("sub.select", { name: s.full_name })} checked={selected} onChange={onSelect} />
      </td>
      <td className="px-3 py-1.5">
        <button type="button" className="block max-w-full truncate text-left font-medium text-slate-100 group-hover:text-signal">{s.full_name}</button>
        <div className="data truncate text-[11.5px] text-slate-500">{s.customer_code ?? `#${s.id}`}</div>
      </td>
      <td className="px-3 py-1.5">
        <div className="truncate text-slate-200" title={s.plan_code ?? undefined}>{plan.name || <span className="text-slate-500">{t("sub.noPlan")}</span>}</div>
        <div className="data text-[11.5px] text-slate-500">{plan.speed ?? ""}</div>
      </td>
      <td className="px-3 py-1.5">
        <Badge tone={SVC_TONE[svc]}>{svcLabel(s, svc, t)}</Badge>
        {att && <div className={cn("mt-0.5 flex items-center gap-1 truncate text-[11.5px]", TONE[ATT_TONE[att]].text)}><FiAlertTriangle className="size-3 shrink-0" /> {attText(att, r, t)}</div>}
      </td>
      <td className="px-3 py-1.5" title={p ? pathTitle(p, t) : undefined}>
        <Badge tone={ui.tone} icon={ui.icon}>{t(`sub.net.${n.net}` as Key)}{n.forMs !== null && n.net !== "cpe_silent" ? ` · ${dur(n.forMs)}` : ""}</Badge>
        <div className="data mt-0.5 truncate text-[11.5px] text-slate-500">
          {n.onts.length > 1 ? t("sub.multi", { n: n.onts.length, up: n.up, down: n.down })
            : p ? path(p.ont)
            : n.source === "cpe" ? [[s.cpe?.manufacturer, s.cpe?.model].filter(Boolean).join(" ") || t("sub.cpeAcs"), s.cpe?.odp].filter(Boolean).join(" · ")
            : t("sub.noDevice")}
        </div>
      </td>
      <td className="px-3 py-1.5"><Signal r={r} /></td>
      <td className="px-3 py-1.5" onClick={(e) => n.source === "none" && canLink && e.stopPropagation()}>
        {n.source === "ont" ? (
          <>
            <div className="text-slate-300">{n.onts.length > 1 ? t("sub.acc.onts", { n: n.onts.length }) : t("sub.acc.ont")}</div>
            <div className="text-[11.5px] text-slate-500">{t(s.onts.every((o) => o.link === "manual") ? "sub.acc.confirmed" : "sub.acc.pppoe")}</div>
          </>
        ) : n.source === "cpe" ? (
          <>
            <div className="text-slate-300">{t("sub.acc.cpe")}</div>
            <div className="text-[11.5px] text-slate-500">{s.cpe?.last_inform_at ? t("sub.acc.inform", { age: durText(now - Date.parse(s.cpe.last_inform_at)) }) : t("sub.acc.neverInform")}</div>
          </>
        ) : canLink ? (
          <Button size="xs" variant="outline" onClick={onLink}><FiLink /> {t("sub.linkOnt")}</Button>
        ) : <span className="text-slate-500">{t("sub.net.unlinked")}</span>}
      </td>
      <td className="pr-2 text-right" onClick={(e) => e.stopPropagation()}>
        <RowMenu r={r} canLink={canLink} onOpen={onOpen} onLink={onLink} />
      </td>
    </tr>
  );
}

function pathTitle(p: OntNet, t: T) {
  const o = p.ont;
  return [
    `${t("sub.path.olt")}: ${o.olt_name}`, `${t("sub.path.pon")}: ${o.pon_path}`, `${t("sub.path.ont")}: #${o.ont_id}`,
    `${t("sub.path.serial")}: ${o.serial}`, `${t("sub.path.distance")}: ${distText(o.distance_m)}`, `${t("sub.path.rx")}: ${fmtDbm(o.rx_power_dbm)}`,
  ].join("\n");
}

function Signal({ r }: { r: Row }) {
  const { t } = useI18n();
  const n = r.n;
  if (n.rx !== null) {
    const tone: Tone = n.net === "critical" ? "crit" : n.net === "weak" ? "warn" : "good";
    return (
      <>
        <div className={cn("data", tone === "good" ? "text-slate-100" : TONE[tone].text)}>{n.rx.toFixed(1)} <span className="text-[11px] text-slate-500">dBm</span></div>
        <div className={cn("text-[11.5px]", tone === "good" ? "text-slate-500" : TONE[tone].text)}>{t(`sub.sig.${tone}` as Key)}</div>
      </>
    );
  }
  const note: Key = n.net === "los" ? "sub.net.los" : n.net === "power" ? "sub.net.power" : n.net === "offline" ? "sub.net.offline"
    : n.net === "stale" ? "sub.sig.stale" : n.source === "cpe" ? "sub.sig.noOptics" : n.source === "none" ? "sub.sig.noOnt" : "sub.sig.noReading";
  return (
    <>
      <div className="text-slate-600">—</div>
      <div className="text-[11.5px] text-slate-500">{t(note)}</div>
    </>
  );
}

function RowMenu({ r, canLink, onOpen, onLink }: { r: Row; canLink: boolean; onOpen: () => void; onLink: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("keydown", close); };
  }, [open]);
  const o = r.n.primary?.ont;
  const alerting = r.s.onts.find((x) => (x.open_alerts ?? 0) > 0);
  const item = (I: IconType, label: string, action: (() => void) | string) => typeof action === "string" ? (
    <Link role="menuitem" to={action} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px] text-slate-200 hover:bg-white/[0.05]"><I className="size-3.5" /> {label}</Link>
  ) : (
    <button type="button" role="menuitem" onClick={() => { setOpen(false); action(); }} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] text-slate-200 hover:bg-white/[0.05]"><I className="size-3.5" /> {label}</button>
  );
  return (
    <div ref={ref} className="relative inline-block">
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={t("sub.actions", { name: r.s.full_name })} onClick={() => setOpen(!open)}
        className="grid size-7 place-items-center rounded-sm text-slate-400 hover:bg-white/5 hover:text-slate-100"><FiMoreHorizontal /></button>
      {open && (
        <div role="menu" className="absolute right-0 z-30 mt-1 w-52 overflow-hidden rounded-md border border-rule bg-[#070b14] py-1 text-left shadow-2xl">
          {item(FiActivity, t("sub.m.open"), onOpen)}
          {o && item(FiRadio, t("sub.m.ont"), `/onts/${o.id}`)}
          {o && item(FiServer, t("sub.m.olt"), `/olts/${o.olt_id}`)}
          {alerting && item(FiAlertTriangle, t("sub.m.alerts"), `/alerts?open=ont:${alerting.id}`)}
          {canLink && item(FiLink, t(r.s.onts.length ? "sub.m.linkAnother" : "sub.linkOnt"), onLink)}
          <div className="my-1 border-t border-rule" />
          {item(FiCopy, t("sub.m.copy"), () => navigator.clipboard?.writeText(r.s.customer_code ?? String(r.s.id)))}
        </div>
      )}
    </div>
  );
}

// ---------- detail drawer ----------

function Drawer({ r, ctx, canLink, onClose, onLink }: { r: Row; ctx: Ctx; canLink: boolean; onClose: () => void; onLink: () => void }) {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const d = useQuery({ queryKey: ["subscriber", r.s.id], queryFn: () => getSubscriber(r.s.id), refetchInterval: 30_000 });
  const [unlink, setUnlink] = useState<SubscriberONT | null>(null);
  const [pinging, setPinging] = useState(false);
  const un = useMutation({
    mutationFn: (o: SubscriberONT) => unlinkSubscriberONT(r.s.id, o.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["subscribers"] }); qc.invalidateQueries({ queryKey: ["subscriber", r.s.id] }); setUnlink(null); },
  });
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const { s, n, att, plan, svc } = r;
  const ui = NET_UI[n.net];

  type Ev = { at: string; title: string; detail: string | null; tone: Tone };
  const evs: Ev[] = [
    ...(d.data?.events ?? []).map((e) => {
      const x = eventTitle(e, t);
      const o = s.onts.find((y) => y.id === e.ont_id);
      return { at: e.occurred_at ?? e.created_at, title: x.title, detail: [x.detail, s.onts.length > 1 && o ? o.serial : null].filter(Boolean).join(" · ") || null,
        tone: (x.tone === "idle" ? "muted" : x.tone) as Tone };
    }),
    ...(d.data?.links ?? []).map((l) => ({ at: l.at, title: t(l.action === "subscriber.link" ? "sub.ev.linked" : "sub.ev.unlinked"), detail: [l.serial, l.by].filter(Boolean).join(" · "), tone: "info" as Tone })),
  ].sort((a, b) => Date.parse(b.at) - Date.parse(a.at)).slice(0, 14);
  const alerts = d.data?.alerts ?? [];

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
      <aside role="dialog" aria-label={s.full_name} onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-[620px] flex-col overflow-y-auto border-l border-rule bg-[#080d17] shadow-2xl">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-rule bg-[#080d17] px-5 py-4">
          <div className="min-w-0">
            <p className="eyebrow">{t("sub.d.subscriber")}</p>
            <h2 className="mt-0.5 truncate text-lg font-semibold text-slate-100">{s.full_name}</h2>
            <p className="data mt-0.5 text-[12px] text-slate-500">
              {s.customer_code}{s.whmcs_client_id && <> · {t("sub.d.client", { id: s.whmcs_client_id })}</>}{s.pppoe_username && <> · PPPoE {s.pppoe_username}</>}
            </p>
          </div>
          <button type="button" aria-label={t("common.close")} onClick={onClose} className="grid size-8 place-items-center rounded-sm text-slate-400 hover:bg-white/5 hover:text-slate-100"><FiX /></button>
        </header>

        <div className="space-y-4 px-5 py-4">
          <div className="grid grid-cols-2 gap-3">
            <section className="rounded-md border border-rule p-3">
              <p className="eyebrow mb-1.5">{t("sub.d.service")}</p>
              <p className="text-[14px] text-slate-100">{plan.name || t("sub.noPlan")}</p>
              <p className="data text-[12px] text-slate-400">{plan.speed ?? ""}</p>
              <div className="mt-2"><Badge tone={SVC_TONE[svc]}>{svcLabel(s, svc, t)}</Badge></div>
              <p className="mt-2 text-[11px] text-slate-500">{t("sub.d.fromWhmcs", { age: durText(ctx.now - Date.parse(s.updated_at)) })}</p>
            </section>
            <section className="rounded-md border border-rule p-3">
              <p className="eyebrow mb-1.5">{t("sub.d.network")}</p>
              <Badge tone={ui.tone} icon={ui.icon}>{t(`sub.net.${n.net}` as Key)}</Badge>
              <p className="mt-2 text-[12px] text-slate-400">
                {n.source === "ont" ? (n.onts.length > 1 ? t("sub.multi", { n: n.onts.length, up: n.up, down: n.down }) : path(n.primary!.ont))
                  : n.source === "cpe" ? t("sub.d.cpeOnlyNote") : t("sub.d.unlinkedNote")}
              </p>
              {n.forMs !== null && <p className="mt-1 text-[12px] text-slate-400">{t(n.source === "cpe" ? "sub.d.silentFor" : "sub.d.downFor", { d: dur(n.forMs) })}</p>}
            </section>
          </div>

          {att && (
            <p className={cn("flex items-start gap-2 rounded-sm border px-3 py-2 text-[12.5px]", ATT_TONE[att] === "crit" ? "border-signal-crit/30 bg-signal-crit/[0.06] text-rose-200" : "border-amber-400/30 bg-amber-400/[0.05] text-amber-100")}>
              <FiAlertTriangle className="mt-0.5 size-3.5 shrink-0" /> <span><b className="font-semibold">{attText(att, r, t)}.</b> {t(`sub.attWhy.${att}` as Key)}</span>
            </p>
          )}

          {alerts.map((a) => (
            <div key={a.id} className="flex items-center gap-3 rounded-sm border border-signal-crit/30 bg-signal-crit/[0.06] px-3 py-2">
              <Dot tone={a.severity === "critical" ? "crit" : "warn"} />
              <div className="min-w-0 flex-1">
                <p className="eyebrow text-rose-200">{t("sub.d.incident")}</p>
                <p className="truncate text-[13px] text-slate-100">{a.message ?? a.metric}</p>
                <p className="text-[11.5px] text-slate-400">{t("sub.d.openFor", { d: durText(ctx.now - Date.parse(a.opened_at)) })}{a.status === "ack" && <> · {t("sub.d.acked")}</>}</p>
              </div>
              <Link to={`/alerts?open=${a.target_type}:${a.target_id}`} className="shrink-0 text-[12px] text-signal hover:underline">{t("sub.d.openIncident")}</Link>
            </div>
          ))}
          {d.data && alerts.length === 0 && n.source === "ont" && <p className="text-[12px] text-slate-500">{t("sub.d.noAlerts")}</p>}

          <section>
            <div className="mb-2 flex items-center justify-between">
              <p className="eyebrow">{t("sub.d.access")}</p>
              <span className="flex items-center gap-4">
                {canLink && (s.pppoe_username || s.cpe) && <button type="button" onClick={() => setPinging(true)} className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-signal"><FiActivity className="size-3" /> {t("cping.title")}</button>}
                {canLink && <button type="button" onClick={onLink} className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-signal"><FiLink className="size-3" /> {t(s.onts.length ? "sub.m.linkAnother" : "sub.linkOnt")}</button>}
              </span>
            </div>
            <div className="space-y-2">
              {n.onts.map((x) => <OntCard key={x.ont.id} x={x} now={ctx.now} canUnlink={canLink && x.ont.link === "manual"} onUnlink={() => setUnlink(x.ont)} />)}
              {s.cpe && (
                <div className="rounded-md border border-rule p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[13px] font-medium text-slate-100">{t("sub.d.cpe")}</p>
                    <span className="text-[11px] text-slate-500">{t(s.cpe.match === "tag" ? "sub.d.viaTag" : "sub.d.viaPppoe", { id: s.external_id ?? "" })}</span>
                  </div>
                  <dl className="mt-2 grid grid-cols-3 gap-x-4 gap-y-2">
                    <F label={t("sub.d.model")}>{[s.cpe.manufacturer, s.cpe.model].filter(Boolean).join(" ") || t("sub.na")}</F>
                    <F label={t("sub.path.serial")}>{s.cpe.serial ?? t("sub.na")}</F>
                    <F label={t("sub.d.lastInform")}>{s.cpe.last_inform_at ? t("ua.ago", { age: durText(ctx.now - Date.parse(s.cpe.last_inform_at)) }) : t("sub.neverSeen")}</F>
                    <F label={t("sub.d.wanIp")}>{s.cpe.wan_ip ?? t("sub.na")}</F>
                    <F label="MAC">{s.cpe.mac ?? t("sub.na")}</F>
                    <F label="ODP">{s.cpe.odp ? s.cpe.odp.split(",").join(", ") : t("sub.d.noOdp")}</F>
                  </dl>
                  {(s.cpe.tag_name || s.cpe.tag_login) && (
                    <p className="mt-2 text-[11.5px] text-slate-500">{t("sub.d.acsTag")}: <span className="data text-slate-300">#{s.external_id} {s.cpe.tag_login} {s.cpe.tag_name}</span></p>
                  )}
                  {s.cpe.tag_login && s.pppoe_username && s.cpe.tag_login.toLowerCase() !== s.pppoe_username.toLowerCase() && (
                    <p className="mt-1 flex items-center gap-1.5 text-[11.5px] text-signal-warn"><FiAlertTriangle className="size-3" /> {t("sub.d.loginDiffers", { tag: s.cpe.tag_login, billing: s.pppoe_username })}</p>
                  )}
                </div>
              )}
              {!s.onts.length && !s.cpe && <p className="rounded-md border border-dashed border-rule px-3 py-4 text-center text-[12.5px] text-slate-500">{t("sub.d.nothing")}</p>}
            </div>
          </section>

          <section>
            <p className="eyebrow mb-2">{t("sub.d.events")}</p>
            {d.isLoading ? <p className="text-[12px] text-slate-500">{t("common.loading")}</p> : evs.length === 0 ? <p className="text-[12px] text-slate-500">{t("sub.d.noEvents")}</p> : (
              <ol className="space-y-1.5">
                {evs.map((e, i) => (
                  <li key={i} className="grid grid-cols-[88px_auto_1fr] items-baseline gap-2.5 text-[12.5px]">
                    <span className="data text-[11px] text-slate-500" title={fmtDateTime(locale, e.at)}>{t("ua.ago", { age: durText(ctx.now - Date.parse(e.at)) })}</span>
                    <Dot tone={e.tone} />
                    <span className="min-w-0 truncate text-slate-200">{e.title}{e.detail && <span className="text-slate-500"> · {e.detail}</span>}</span>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <p className="border-t border-rule pt-3 text-[11.5px] text-slate-500">
            {t("sub.d.sourceNote")}{" "}
            <button type="button" onClick={() => navigator.clipboard?.writeText(s.customer_code ?? String(s.id))} className="inline-flex items-center gap-1 text-slate-400 hover:text-signal"><FiCopy className="size-3" /> {t("sub.m.copy")}</button>
          </p>
        </div>
      </aside>
      {pinging && <CustomerPingDialog kind="subscribers" id={s.id} name={s.full_name} onClose={() => setPinging(false)} />}
      {unlink && (
        <Dialog open onOpenChange={(o) => !o && setUnlink(null)}>
          <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle>{t("sub.unlinkTitle")}</DialogTitle>
              <DialogDescription className="text-slate-400">{t("sub.unlinkBody", { serial: unlink.serial, name: s.full_name })}</DialogDescription>
            </DialogHeader>
            {un.error && <p className="text-sm text-signal-crit">{(un.error as Error).message}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setUnlink(null)}>{t("common.cancel")}</Button>
              <Button variant="destructive" disabled={un.isPending} onClick={() => un.mutate(unlink)}>{t("sub.unlink")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const F = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="min-w-0"><dt className="text-[11px] text-slate-500">{label}</dt><dd className="data mt-0.5 truncate text-[12.5px] text-slate-100">{children}</dd></div>
);

function OntCard({ x, now, canUnlink, onUnlink }: { x: OntNet; now: number; canUnlink: boolean; onUnlink: () => void }) {
  const { t } = useI18n();
  const o = x.ont;
  const ui = NET_UI[x.net];
  return (
    <div className="rounded-md border border-rule p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-[13px] font-medium text-slate-100">{o.olt_name}</p>
        <Badge tone={ui.tone} icon={ui.icon}>{t(`sub.net.${x.net}` as Key)}{x.downMs !== null ? ` · ${dur(x.downMs)}` : ""}</Badge>
      </div>
      <dl className="mt-2 grid grid-cols-4 gap-x-4 gap-y-2">
        <F label={t("sub.path.pon")}>{o.pon_path}</F>
        <F label={t("sub.path.ont")}>#{o.ont_id}</F>
        <F label={t("sub.path.serial")}>{o.serial}</F>
        <F label={t("sub.path.distance")}>{o.distance_m !== null ? distText(o.distance_m) : t("sub.na")}</F>
        <F label={t("sub.d.ontRx")}>{fmtDbm(o.rx_power_dbm)}</F>
        <F label={t("sub.d.oltRx")}>{fmtDbm(o.olt_rx_dbm)}</F>
        <F label={t("sub.d.lastSeen")}>{o.last_seen_at ? t("ua.ago", { age: durText(now - Date.parse(o.last_seen_at)) }) : t("sub.neverSeen")}</F>
        <F label={t("sub.d.link")}>{t(o.link === "manual" ? "sub.d.linkManual" : "sub.d.linkAuto")}</F>
      </dl>
      {o.description && <p className="mt-2 truncate text-[11.5px] text-slate-500">{o.description}</p>}
      <div className="mt-2.5 flex flex-wrap items-center gap-3 border-t border-rule/60 pt-2 text-[12px]">
        <Link to={`/onts/${o.id}`} className="inline-flex items-center gap-1 text-slate-300 hover:text-signal"><FiExternalLink className="size-3" /> {t("sub.m.ont")}</Link>
        <Link to={`/onts/${o.id}?tab=optical`} className="text-slate-300 hover:text-signal">{t("sub.d.signalHistory")}</Link>
        <Link to={`/olts/${o.olt_id}`} className="text-slate-300 hover:text-signal">{t("sub.m.olt")}</Link>
        {canUnlink && <button type="button" onClick={onUnlink} className="ml-auto text-slate-500 hover:text-rose-300">{t("sub.unlink")}</button>}
      </div>
    </div>
  );
}

// ---------- link ONT ----------

function LinkDialog({ s, ctx, onClose }: { s: Subscriber; ctx: Ctx; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  useEffect(() => { const id = setTimeout(() => setDq(q.trim()), 250); return () => clearTimeout(id); }, [q]);
  const sug = useQuery({ queryKey: ["sub-candidates", s.id, ""], queryFn: () => subscriberCandidates(s.id) });
  const res = useQuery({ queryKey: ["sub-candidates", s.id, dq], queryFn: () => subscriberCandidates(s.id, dq), enabled: dq.length >= 2 });
  const [confirm, setConfirm] = useState<ONTCandidate | null>(null);
  const link = useMutation({
    mutationFn: (o: ONTCandidate) => linkSubscriberONT(s.id, o.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscribers"] });
      qc.invalidateQueries({ queryKey: ["subscriber", s.id] });
      qc.invalidateQueries({ queryKey: ["onts"] });
      onClose();
    },
  });
  const list = dq.length >= 2 ? res.data?.results : sug.data?.suggested;
  const loading = dq.length >= 2 ? res.isLoading : sug.isLoading;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("sub.l.title")}</DialogTitle>
          <DialogDescription className="text-slate-400">
            <span className="text-slate-100">{s.full_name}</span> <span className="data">· {s.customer_code}</span>{s.plan_code && <> · {s.plan_code}</>}
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <FiSearch className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-slate-500" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("sub.l.search")} className="h-8 pl-8 text-[12.5px]" />
        </div>
        <p className="eyebrow">{dq.length >= 2 ? t("sub.l.results") : t("sub.l.suggested")}</p>
        {dq.length < 2 && <p className="-mt-1 text-[11.5px] text-slate-500">{t("sub.l.suggestNote")}</p>}
        <div className="max-h-[46vh] space-y-2 overflow-y-auto pr-1">
          {loading && <p className="text-[12.5px] text-slate-500">{t("common.loading")}</p>}
          {!loading && (list ?? []).length === 0 && <p className="py-4 text-center text-[12.5px] text-slate-500">{t(dq.length >= 2 ? "sub.l.none" : "sub.l.noSuggest")}</p>}
          {(list ?? []).map((o) => {
            const x = ontNet({ ...o, link: "manual" }, ctx);
            const ui = NET_UI[x.net];
            const taken = o.linked_to && o.linked_to.id !== s.id;
            const picked = confirm?.id === o.id;
            return (
              <div key={o.id} className={cn("rounded-md border p-3", picked ? "border-signal/60 bg-signal/[0.04]" : "border-rule")}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="data text-[13px] text-slate-100">{o.serial}</p>
                    <p className="text-[12px] text-slate-400">{o.olt_name} · {t("sub.path.pon")} {o.pon_path} · {t("sub.path.ont")} #{o.ont_id}</p>
                    <p className="truncate text-[12px] text-slate-300">{o.description || <span className="text-slate-500">{t("sub.l.noDesc")}</span>}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <Badge tone={ui.tone} icon={ui.icon}>{t(`sub.net.${x.net}` as Key)}</Badge>
                    <p className="data mt-1 text-[12px] text-slate-400">RX {fmtDbm(o.rx_power_dbm)}</p>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-3 border-t border-rule/60 pt-2 text-[12px]">
                  {o.confidence === "strong" && <Badge tone="good">{t("sub.l.strong")}</Badge>}
                  {o.confidence === "possible" && <Badge tone="warn">{t("sub.l.possible")}</Badge>}
                  <span className="min-w-0 flex-1 text-slate-500">
                    {taken ? <span className="text-signal-warn">{t("sub.l.taken", { name: o.linked_to!.full_name })}</span>
                      : o.reason === "cpe_serial" ? t("sub.l.whyCpe") : o.reason === "name" ? t("sub.l.whyName") : ""}
                  </span>
                  {!taken && (picked ? (
                    <>
                      <Button size="xs" variant="ghost" onClick={() => setConfirm(null)}>{t("common.cancel")}</Button>
                      <Button size="xs" disabled={link.isPending} onClick={() => link.mutate(o)}><FiLink /> {t("sub.l.confirm")}</Button>
                    </>
                  ) : <Button size="xs" variant="outline" onClick={() => setConfirm(o)}>{t(o.confidence === "possible" ? "sub.l.review" : "sub.linkOnt")}</Button>)}
                </div>
                {picked && <p className="mt-2 text-[11.5px] text-slate-400">{t("sub.l.confirmNote", { serial: o.serial, name: s.full_name })}</p>}
              </div>
            );
          })}
        </div>
        {link.error && <p className="text-sm text-signal-crit">{(link.error as Error).message}</p>}
      </DialogContent>
    </Dialog>
  );
}

